import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {DAYS_MAP} from '../constants/alertSettingsConfig.ts';
import {useAlertSettingsForm} from './useAlertSettingsForm.tsx';
import {useAlertValidation} from './useAlertValidation.tsx';
import {useNotificationManagement} from './useNotificationManagement.tsx';
import {useAlertSettingsActions} from './useAlertSettingsActions.tsx';

interface AlertSettingsProps {
  onClose: () => void;
  alertId: string;
  open: boolean;
  reload: () => Promise<void>;
  devices: any[];
  emails: any[];
  onSaveAndOpenNotification?: (newAlertId: string) => void;
}

/**
 * アラート設定ダイアログの統合フック
 *
 * このフックは以下の4つのサブフックを組み合わせて、
 * アラート設定ダイアログの完全な機能を提供します：
 *
 * 1. useAlertSettingsForm - フォーム状態管理
 * 2. useAlertValidation - バリデーションロジック
 * 3. useNotificationManagement - 通知先管理
 * 4. useAlertSettingsActions - API操作
 *
 * @refactored 2025-10-14 - Proposal 2-1: Split massive 991-line hook into focused modules
 */
export const useAlertSettingsDialog = ({
  onClose,
  alertId,
  open,
  reload,
  devices,
  emails,
  onSaveAndOpenNotification
}: AlertSettingsProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();

  // ========================================
  // Sub-hooks instantiation
  // ========================================

  // 1. Form state management
  const formState = useAlertSettingsForm();

  // 2. Validation logic
  const validation = useAlertValidation({t, enqueueSnackbar});

  // 3. Notification management
  const notificationMgmt = useNotificationManagement({
    t,
    enqueueSnackbar,
    emails,
    updateValidationError: validation.updateValidationError
  });

  // 4. API operations and data loading
  const actions = useAlertSettingsActions({
    alertId,
    open,
    onClose,
    reload,
    enqueueSnackbar,
    emails,
    onSaveAndOpenNotification,

    // Form values
    active: formState.active,
    alertName: formState.alertName,
    machine: formState.machine,
    rule: formState.rule,
    processType: formState.processType,
    scheduleType: formState.scheduleType,
    days: formState.days,
    from: formState.from,
    to: formState.to,
    interval: formState.interval,
    fixTime: formState.fixTime,
    isPdf: formState.isPdf,

    // Form setters
    setActive: formState.setActive,
    setAlertName: formState.setAlertName,
    setMachine: formState.setMachine,
    setRule: formState.setRule,
    setProcessType: formState.setProcessType,
    setScheduleType: formState.setScheduleType,
    setDays: formState.setDays,
    setFrom: formState.setFrom,
    setTo: formState.setTo,
    setInterval: formState.setInterval,
    setFixTime: formState.setFixTime,
    setIsPdf: formState.setIsPdf,

    // Notification management
    notifications: notificationMgmt.notifications,
    setNotifications: notificationMgmt.setNotifications,

    // Validation
    validateAll: validation.validateAll,

    // Reset functions
    resetForm: formState.resetForm,
    resetNotifications: notificationMgmt.resetNotifications,
    clearValidationErrors: validation.clearValidationErrors
  });

  // ========================================
  // Validation-aware field handlers
  // ========================================

  /**
   * アラート名変更ハンドラー（バリデーション付き）
   */
  const handleAlertNameChange = (value: string) => {
    formState.setAlertName(value);
    const error = validation.validateField('alertName', value);
    validation.updateValidationError('alertName', error);
  };

  /**
   * 監視装置変更ハンドラー（バリデーション付き）
   */
  const handleMachineChange = (value: string) => {
    formState.setMachine(value);
    const error = validation.validateField('machine', value);
    validation.updateValidationError('machine', error);
  };

  /**
   * 期間From変更ハンドラー（バリデーション付き）
   */
  const handleFromChange = (value: string) => {
    formState.setFrom(value);
    const ctx = {
      from: value,
      to: formState.to,
      scheduleType: formState.scheduleType,
      interval: formState.interval
    };
    const fromError = validation.validateField('from', value, ctx);
    const toError = validation.validateField('to', formState.to, ctx);
    const rangeError = validation.validateField('timeRange', null, ctx);
    validation.updateValidationErrors({
      from: fromError,
      to: toError,
      timeRange: rangeError
    });
  };

  /**
   * 期間To変更ハンドラー（バリデーション付き）
   */
  const handleToChange = (value: string) => {
    formState.setTo(value);
    const ctx = {
      from: formState.from,
      to: value,
      scheduleType: formState.scheduleType,
      interval: formState.interval
    };
    const fromError = validation.validateField('from', formState.from, ctx);
    const toError = validation.validateField('to', value, ctx);
    const rangeError = validation.validateField('timeRange', null, ctx);
    validation.updateValidationErrors({
      from: fromError,
      to: toError,
      timeRange: rangeError
    });
  };

  /**
   * 固定時刻変更ハンドラー（バリデーション付き）
   */
  const handleFixTimeChange = (value: string) => {
    formState.setFixTime(value);
    const error = validation.validateField('fixTime', value, {
      scheduleType: formState.scheduleType
    });
    validation.updateValidationError('fixTime', error);
  };

  /**
   * 判定頻度変更ハンドラー（バリデーション付き）
   */
  const handleIntervalChange = (value: string) => {
    formState.setInterval(value);
    const rangeError = validation.validateField('timeRange', null, {
      from: formState.from,
      to: formState.to,
      scheduleType: formState.scheduleType,
      interval: value
    });
    validation.updateValidationError('timeRange', rangeError);
  };

  /**
   * スケジュールタイプ変更ハンドラー（バリデーション付き）
   */
  const handleScheduleTypeChange = (value: string) => {
    formState.setScheduleType(value);
    // スケジュールタイプ切り替え時にエラーをクリア
    validation.updateValidationErrors({
      from: undefined,
      to: undefined,
      fixTime: undefined,
      timeRange: undefined
    });
  };

  // ========================================
  // Computed properties
  // ========================================

  /**
   * 保存ボタンのdisabled判定
   */
  const disabled =
    formState.alertName === '' ||
    formState.alertName.length > 100 ||
    formState.machine === '' ||
    formState.rule === '' ||
    formState.days.length === 0 ||
    (formState.scheduleType === '1' && (formState.from === '' || formState.to === '')) ||
    (formState.scheduleType === '2' && formState.fixTime === '') ||
    Object.values(validation.validationErrors).some(error => error !== undefined);

  // ========================================
  // Return combined interface
  // ========================================

  return {
    // API operations
    disabled,
    onSubmit: actions.onSubmit,
    onDelete: actions.onDelete,
    isLoading: actions.isLoading,

    // Form state
    active: formState.active,
    setActive: formState.setActive,
    alertName: formState.alertName,
    setAlertName: formState.setAlertName,
    machine: formState.machine,
    setMachine: formState.setMachine,
    devices,
    rule: formState.rule,
    setRule: formState.setRule,
    processType: formState.processType,
    setProcessType: formState.setProcessType,
    scheduleType: formState.scheduleType,
    setScheduleType: formState.setScheduleType,
    from: formState.from,
    setFrom: formState.setFrom,
    to: formState.to,
    setTo: formState.setTo,
    fixTime: formState.fixTime,
    setFixTime: formState.setFixTime,
    isPdf: formState.isPdf,
    setIsPdf: formState.setIsPdf,
    DAYS_MAP,
    days: formState.days,
    setDays: formState.setDays,
    interval: formState.interval,
    setInterval: formState.setInterval,

    // Notification management
    notifications: notificationMgmt.notifications,
    emails,
    t,
    notificationListDialogOpen: notificationMgmt.notificationListDialogOpen,
    handleOpenNotificationListDialog: notificationMgmt.handleOpenNotificationListDialog,
    handleCloseNotificationListDialog: notificationMgmt.handleCloseNotificationListDialog,
    handleNotificationsChange: notificationMgmt.handleNotificationsChange,

    // Notification add dialog
    addDialogOpen: notificationMgmt.addDialogOpen,
    handleOpenAddDialog: notificationMgmt.handleOpenAddDialog,
    handleCloseAddDialog: notificationMgmt.handleCloseAddDialog,

    // Inline editing
    handleInlineSendSettingChange: notificationMgmt.handleInlineSendSettingChange,
    handleInlineDelete: notificationMgmt.handleInlineDelete,
    handleInlineAdd: notificationMgmt.handleInlineAdd,
    newEmail: notificationMgmt.newEmail,
    setNewEmail: notificationMgmt.setNewEmail,
    newReceiveMailType: notificationMgmt.newReceiveMailType,
    setNewReceiveMailType: notificationMgmt.setNewReceiveMailType,

    // Batch operations
    selectedIndices: notificationMgmt.selectedIndices,
    handleToggleSelect: notificationMgmt.handleToggleSelect,
    handleToggleSelectAll: notificationMgmt.handleToggleSelectAll,
    handleBatchDelete: notificationMgmt.handleBatchDelete,
    handleBatchSendSettingChange: notificationMgmt.handleBatchSendSettingChange,

    // Validation
    validationErrors: validation.validationErrors,
    validateAll: validation.validateAll,
    handleAlertNameChange,
    handleMachineChange,
    handleFromChange,
    handleToChange,
    handleFixTimeChange,
    handleIntervalChange,
    handleScheduleTypeChange
  };
};
